// Background Music Handler Module
// Manages music upload, preview, and playback control

let backgroundMusicData = {
    file: null,
    autoplay: true,
    loop: true
};

// Make backgroundMusicData accessible globally for payment handlers
window.backgroundMusicData = backgroundMusicData;

let musicPreviewAudio = null;
let handlersAttached = false;

/**
 * Find a music element by id, preferring the PPM sub-panel over the old sidebar.
 * Both containers render the same background-music.html template so IDs are duplicated.
 */
function findMusicEl(id) {
    var ppmBox = document.querySelector('.ppm-content-box[data-ppm-tab="background-music"]');
    if (ppmBox) {
        var ppmEl = ppmBox.querySelector('#' + id);
        if (ppmEl) return ppmEl;
    }
    return document.getElementById(id);
}

/**
 * Initialize background music handlers
 */
window.initializeBackgroundMusicHandlers = function() {
    // Prevent duplicate initialization
    if (handlersAttached) return;

    // Always bind the mute/unmute button — it must work even when the
    // user-upload feature is disabled (e.g. theme has its own built-in music).
    const musicBtn = document.getElementById('musicBtn');
    if (musicBtn && !musicBtn._musicMuteBound) {
        musicBtn._musicMuteBound = true;
        musicBtn.addEventListener('click', toggleIframeMusic);
    }

    // Prefer the PPM element for a quick existence check
    const fileInput = findMusicEl('backgroundMusicFile');

    if (!fileInput) {
        // Only warn and watch for the input if the background_music_feature is enabled.
        // When the feature is intentionally off, the input is never rendered — no need to retry.
        const featureEnabled = window.themeInfo && window.themeInfo.background_music_feature;
        if (featureEnabled) {
            console.warn('Background music file input not found - will retry when features tab is shown');
            const observer = new MutationObserver((mutations, obs) => {
                if (findMusicEl('backgroundMusicFile')) {
                    obs.disconnect();
                    setTimeout(() => window.initializeBackgroundMusicHandlers(), 100);
                }
            });
            observer.observe(document.body, { childList: true, subtree: true });
        }
        return;
    }

    handlersAttached = true;

    // Bind the file-change handler to ALL copies of #backgroundMusicFile in the DOM
    // (one in the PPM sub-panel, one in the old sidebar features-form).
    document.querySelectorAll('[id="backgroundMusicFile"]').forEach(function(input) {
        if (!input._musicChangeBound) {
            input._musicChangeBound = true;
            input.addEventListener('change', handleMusicFileUpload);
        }
    });

    // Bind the upload-area click → media picker for ALL copies of #musicUploadInput.
    document.querySelectorAll('[id="musicUploadInput"]').forEach(function(uploadArea) {
        if (!uploadArea._musicPickerBound) {
            uploadArea._musicPickerBound = true;
            uploadArea.addEventListener('click', function(e) {
                if (typeof window.openMediaPicker === 'function') {
                    e.preventDefault();
                    e.stopPropagation();
                    // Find the file input that lives in the SAME container as this upload area
                    var section = uploadArea.closest('#musicUploadSection') || uploadArea.closest('.card-content-box') || uploadArea.parentElement;
                    var scopedInput = section ? section.querySelector('[id="backgroundMusicFile"]') : null;
                    window.openMediaPicker(scopedInput || findMusicEl('backgroundMusicFile'), null);
                }
            });
        }
    });

    // Preview audio — use the PPM copy as the authoritative player
    musicPreviewAudio = findMusicEl('musicPreviewPlayer');
    // Also bind timeupdate/ended on ALL copies
    document.querySelectorAll('[id="musicPreviewPlayer"]').forEach(function(audio) {
        if (!audio._musicPlayerBound) {
            audio._musicPlayerBound = true;
            audio.addEventListener('timeupdate', function() {
                var fill = findMusicEl('bmProgressFill');
                if (fill && audio.duration) {
                    fill.style.width = (audio.currentTime / audio.duration * 100) + '%';
                }
            });
            audio.addEventListener('ended', function() {
                var btn  = findMusicEl('previewPlayBtn');
                var wave = findMusicEl('bmWave');
                var fill = findMusicEl('bmProgressFill');
                if (btn)  btn.innerHTML = '<i class="bi bi-play-fill"></i>';
                if (wave) wave.classList.remove('playing');
                if (fill) fill.style.width = '0%';
            });
        }
    });

    // Remove, play, autoplay, loop, mute controls — bind to ALL copies
    document.querySelectorAll('[id="removeMusicBtn"]').forEach(function(btn) {
        if (!btn._musicRemoveBound) { btn._musicRemoveBound = true; btn.addEventListener('click', removeMusicFile); }
    });
    document.querySelectorAll('[id="previewPlayBtn"]').forEach(function(btn) {
        if (!btn._musicPlayBound) { btn._musicPlayBound = true; btn.addEventListener('click', toggleMusicPreview); }
    });
    document.querySelectorAll('[id="autoplayMusic"]').forEach(function(chk) {
        if (!chk._musicAutoplayBound) {
            chk._musicAutoplayBound = true;
            chk.addEventListener('change', function() {
                backgroundMusicData.autoplay = this.checked;
                saveBackgroundMusicToStorage(); updateIframeMusic();
            });
        }
    });
    document.querySelectorAll('[id="loopMusic"]').forEach(function(chk) {
        if (!chk._musicLoopBound) {
            chk._musicLoopBound = true;
            chk.addEventListener('change', function() {
                backgroundMusicData.loop = this.checked;
                saveBackgroundMusicToStorage(); updateIframeMusic();
            });
        }
    });

    loadBackgroundMusicFromStorage();
};

/**
 * Handle music file upload
 */
function handleMusicFileUpload(event) {
    const file = event.target.files[0];
    
    if (!file) return;

    // console.log('Music file selected:', file.name, file.type, file.size);

    // Validate file type
    const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg'];
    if (!validTypes.includes(file.type)) {
        alert('Please upload a valid audio file (MP3, WAV, or OGG)');
        return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
        alert('File size must be less than 10MB');
        return;
    }

    // Store file information temporarily (file will be uploaded during draft save or payment)
    backgroundMusicData.file = file;
    backgroundMusicData.fileName = file.name;
    backgroundMusicData.fileSize = formatFileSize(file.size);
    
    // Create file URL for preview
    const fileUrl = URL.createObjectURL(file);
    backgroundMusicData.fileUrl = fileUrl;

    // console.log('Music file selected (will upload during draft/payment):', backgroundMusicData);

    // Update UI immediately
    displayMusicPreview();
    
    // Show music control button
    showMusicControlButton();
    
    // Save metadata to storage (without uploading file yet)
    saveBackgroundMusicToStorage();
    
    // Update iframe music
    updateIframeMusic();
}

/**
 * Display music preview UI
 */
function displayMusicPreview() {
    const uploadInput = findMusicEl('musicUploadInput');
    const musicPreview = findMusicEl('musicPreview');
    const musicOptions = findMusicEl('musicOptions');

    if (uploadInput) uploadInput.classList.add('d-none');
    if (musicPreview) musicPreview.classList.remove('d-none');
    if (musicOptions) musicOptions.classList.remove('d-none');

    // Reset play button, waveform, and progress to idle state
    const previewPlayBtn = findMusicEl('previewPlayBtn');
    if (previewPlayBtn) previewPlayBtn.innerHTML = '<i class="bi bi-play-fill"></i>';
    const wave = findMusicEl('bmWave');
    if (wave) wave.classList.remove('playing');
    const fill = findMusicEl('bmProgressFill');
    if (fill) fill.style.width = '0%';

    // Re-grab audio reference from the PPM container
    musicPreviewAudio = findMusicEl('musicPreviewPlayer');
    if (musicPreviewAudio && backgroundMusicData.fileUrl) {
        musicPreviewAudio.src = backgroundMusicData.fileUrl;
        musicPreviewAudio.loop = false;
        musicPreviewAudio.load();
    }
}

/**
 * Remove music file
 */
function removeMusicFile() {
    // Stop any playing audio
    if (musicPreviewAudio) {
        musicPreviewAudio.pause();
        musicPreviewAudio.src = '';
    }

    // Only revoke the object URL if the file is NOT in the shared media library.
    // Files selected from the picker have their URL owned by media-upload.js's
    // objectURLCache; revoking here would permanently break re-selection of the
    // same file (the cached URL becomes dead and can never be played again).
    const fileInLibrary = Array.isArray(window.mediaUploadFiles) &&
                          backgroundMusicData.file &&
                          window.mediaUploadFiles.includes(backgroundMusicData.file);
    if (backgroundMusicData.fileUrl && !fileInLibrary) {
        URL.revokeObjectURL(backgroundMusicData.fileUrl);
    }

    // Clear file input (using the PPM-preferred element).
    const wasServerFile = !backgroundMusicData.file && !!backgroundMusicData.fileUrl;
    const fileInput = findMusicEl('backgroundMusicFile');
    if (fileInput) {
        fileInput.value = '';
        if (wasServerFile) {
            fileInput.setAttribute('data-file-action', 'delete');
        } else {
            fileInput.removeAttribute('data-file-action');
            fileInput._selectedFiles = null;
        }
    }

    // Reset data IN-PLACE so window.backgroundMusicData reference stays valid
    // (re-assigning backgroundMusicData = {} would break the reference used by media-upload.js)
    backgroundMusicData.file     = null;
    backgroundMusicData.fileName = null;
    backgroundMusicData.fileSize = null;
    backgroundMusicData.fileUrl  = null;
    backgroundMusicData.autoplay = true;
    backgroundMusicData.loop     = true;

    // Update UI
    const uploadInput = findMusicEl('musicUploadInput');
    const musicPreview = findMusicEl('musicPreview');
    const musicOptions = findMusicEl('musicOptions');

    if (uploadInput) uploadInput.classList.remove('d-none');
    if (musicPreview) musicPreview.classList.add('d-none');
    if (musicOptions) musicOptions.classList.add('d-none');

    // Reset waveform, play button and progress bar
    const wave = findMusicEl('bmWave');
    if (wave) wave.classList.remove('playing');
    const previewPlayBtn = findMusicEl('previewPlayBtn');
    if (previewPlayBtn) previewPlayBtn.innerHTML = '<i class="bi bi-play-fill"></i>';
    const fill = findMusicEl('bmProgressFill');
    if (fill) fill.style.width = '0%';

    // Hide music control button
    hideMusicControlButton();

    // Remove from storage
    saveBackgroundMusicToStorage();
    
    // Update iframe
    updateIframeMusic();
}

/**
 * Toggle music preview playback
 */
function toggleMusicPreview() {
    const previewPlayBtn = findMusicEl('previewPlayBtn');
    const wave           = findMusicEl('bmWave');
    // Re-sync audio ref to PPM's player
    musicPreviewAudio = findMusicEl('musicPreviewPlayer') || musicPreviewAudio;

    if (!musicPreviewAudio || !backgroundMusicData.fileUrl) return;

    if (musicPreviewAudio.paused) {
        musicPreviewAudio.play().catch(() => {});
        if (previewPlayBtn) previewPlayBtn.innerHTML = '<i class="bi bi-pause-fill"></i>';
        if (wave) wave.classList.add('playing');
    } else {
        musicPreviewAudio.pause();
        if (previewPlayBtn) previewPlayBtn.innerHTML = '<i class="bi bi-play-fill"></i>';
        if (wave) wave.classList.remove('playing');
    }
}

/**
 * Format file size to human-readable format
 */
function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Upload music file to server (called during draft save or payment)
 * @param {string} invitationType - 'draft' or 'invitation'
 * @param {number} objectId - Draft ID or Invitation ID
 * @returns {Promise<Object>} - Upload result with fileUrl
 */
async function uploadMusicToServer(invitationType, objectId) {
    try {
        if (!backgroundMusicData.file) {
            console.warn('No music file to upload');
            return null;
        }
        
        // console.log(`Uploading music file to server for ${invitationType} ${objectId}...`);
        
        // Get CSRF token
        const csrftoken = typeof getCookie === 'function' ? getCookie('csrftoken') : window.getCookie('csrftoken');
        
        // Create FormData
        const formData = new FormData();
        formData.append('music_file', backgroundMusicData.file);
        formData.append('type', invitationType);
        formData.append('object_id', objectId);
        
        // Upload to server
        const response = await fetch('/invitation/upload-music/', {
            method: 'POST',
            headers: {
                'X-CSRFToken': csrftoken
            },
            body: formData
        });
        
        const data = await response.json();
        
        if (data.success) {
            // console.log('✅ Music file uploaded successfully:', data);
            return data;
        } else {
            console.error('Failed to upload music:', data.error);
            throw new Error(data.error);
        }
    } catch (error) {
        console.error('Error uploading music file:', error);
        throw error;
    }
}

// Export the upload function so it can be called by payment/draft handlers
window.uploadBackgroundMusic = uploadMusicToServer;

/**
 * Save background music data to storage
 */
function saveBackgroundMusicToStorage() {
    // Save metadata only (file will be uploaded during draft save or payment)
    const storageData = {
        fileName: backgroundMusicData.fileName,
        fileSize: backgroundMusicData.fileSize,
        autoplay: backgroundMusicData.autoplay,
        loop: backgroundMusicData.loop,
        _hasPendingFile: !!backgroundMusicData.file // Flag indicating file needs upload
    };
    
    if (typeof window.updateFormDataField === 'function') {
        window.updateFormDataField('backgroundMusic', storageData);
        // console.log('Background music metadata saved to storage (pending upload):', storageData.fileName);
    } else {
        console.warn('updateFormDataField function not available');
    }
}

/**
 * Load background music data from storage
 */
function loadBackgroundMusicFromStorage() {
    let storedData = null;

    // Try to get from form data storage
    if (typeof window.getFormDataField === 'function') {
        storedData = window.getFormDataField('backgroundMusic');
    }

    if (!storedData) {
        // Fallback: load from server-embedded stored invitation data (update-invite page).
        // storedInvitationData.data.files.background_music is already a full media URL
        // (constructed by the update_invite view before JSON-dumping storedInvitationData).
        var sd = window.storedInvitationData;
        if (sd && sd.success && sd.data && sd.data.files && sd.data.files.background_music) {
            backgroundMusicData.fileUrl  = sd.data.files.background_music;
            backgroundMusicData.fileName = 'background_music';
            displayMusicPreview();
            showMusicControlButton();
            updateIframeMusic();
        }
        return;
    }

    // Restore music options — update PPM-preferred checkboxes
    if (storedData.autoplay !== undefined) {
        backgroundMusicData.autoplay = storedData.autoplay;
        const autoplayCheckbox = findMusicEl('autoplayMusic');
        if (autoplayCheckbox) autoplayCheckbox.checked = storedData.autoplay;
    }

    if (storedData.loop !== undefined) {
        backgroundMusicData.loop = storedData.loop;
        const loopCheckbox = findMusicEl('loopMusic');
        if (loopCheckbox) loopCheckbox.checked = storedData.loop;
    }

    // Restore file info from URL
    if (storedData.fileUrl && storedData.fileName) {
        backgroundMusicData.fileName = storedData.fileName;
        backgroundMusicData.fileSize = storedData.fileSize;
        backgroundMusicData.fileUrl = storedData.fileUrl;
        backgroundMusicData.tempFileId = storedData.tempFileId;
        
        displayMusicPreview();
        showMusicControlButton();
        updateIframeMusic();
    }
}

/**
 * Update music in iframe
 */
function updateIframeMusic() {
    const iframe = document.getElementById('theme-iframe');
    if (!iframe || !iframe.contentWindow) return;

    const musicData = {
        hasMusic: !!backgroundMusicData.fileUrl,
        fileUrl: backgroundMusicData.fileUrl,   
        autoplay: backgroundMusicData.autoplay,
        loop: backgroundMusicData.loop
    };  

    // Send message to iframe
    iframe.contentWindow.postMessage({
        type: 'UPDATE_BACKGROUND_MUSIC',
        data: musicData
    }, '*');
}

/**
 * Toggle iframe music mute/unmute.
 * Covers: page audio (#myAudio), preview audio, video previews, iframe media, postMessage.
 */
function toggleIframeMusic() {
    const musicBtn = document.getElementById('musicBtn');

    const wasMuted = musicBtn && musicBtn.classList.contains('muted');
    const targetMuted = !wasMuted;

    setMusicBtnState(targetMuted);

    // ── Page-level background audio (#myAudio on invite page) ────
    const pageAudio = document.getElementById('myAudio');
    if (pageAudio) {
        pageAudio.muted = targetMuted;
        if (!targetMuted && pageAudio.paused) {
            pageAudio.play().catch(function () {});
        }
    }

    // ── PPM preview audio (create/update page) ────────────────────
    if (musicPreviewAudio && backgroundMusicData.fileUrl) {
        if (!targetMuted && musicPreviewAudio.paused) {
            musicPreviewAudio.src = backgroundMusicData.fileUrl;
            musicPreviewAudio.loop = backgroundMusicData.loop;
            musicPreviewAudio.play().catch(function () {});
        }
        musicPreviewAudio.muted = targetMuted;
    }

    // ── All <audio> and <video> elements directly in the page ────
    document.querySelectorAll('audio, video').forEach(function (media) {
        if (media === pageAudio) return;          // already handled
        if (media === musicPreviewAudio) return;  // already handled
        if (media.closest('#ppm-popup')) return;  // skip PPM internal players
        media.muted = targetMuted;
        if (!targetMuted && media.paused && media.src) {
            media.play().catch(function () {});
        }
        // Sync per-card mute button icon for video previews
        const card = media.closest('.media-preview-card');
        if (card) {
            const muteBtn = card.querySelector('.media-mute-button');
            if (muteBtn) {
                const icon = muteBtn.querySelector('i');
                if (icon) icon.className = targetMuted ? 'bi bi-volume-mute-fill' : 'bi bi-volume-up-fill';
            }
        }
    });

    // ── Media inside the theme iframe (same-origin only) ─────────
    // If the "Mute theme's built-in music" toggle is on, theme iframe stays muted
    // regardless of the mute/unmute button — only user-added music follows the button.
    const iframe = document.getElementById('theme-iframe');
    const muteThemeToggle = document.querySelector('[id="muteThemeMusic"]');
    const muteThemeMusic = muteThemeToggle ? muteThemeToggle.checked : false;
    const effectiveMuteForTheme = targetMuted || muteThemeMusic;
    try {
        const iDoc = iframe && (iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document));
        if (iDoc) {
            iDoc.querySelectorAll('audio, video').forEach(function (media) {
                media.muted = effectiveMuteForTheme;
                if (!effectiveMuteForTheme && media.paused) media.play().catch(function () {});
            });
            iDoc.querySelectorAll('[data-background-sound-mute]').forEach(function (el) {
                el.setAttribute('data-background-sound-mute', effectiveMuteForTheme ? 'true' : 'false');
            });
        }
    } catch (e) {
        // Cross-origin iframe — postMessage handles it below
    }

    // ── PostMessage to iframe (handles cross-origin themes) ───────
    if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({ type: 'TOGGLE_BACKGROUND_MUSIC', muted: effectiveMuteForTheme }, '*');
    }
}

/**
 * Update the music button icon/class to reflect muted state
 */
function setMusicBtnState(isMuted) {
    const musicBtn = document.getElementById('musicBtn');
    if (!musicBtn) return;

    if (isMuted) {
        musicBtn.classList.add('muted');
        musicBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-volume-x-icon lucide-volume-x"><path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"/><line x1="22" x2="16" y1="9" y2="15"/><line x1="16" x2="22" y1="9" y2="15"/></svg>';
    } else {
        musicBtn.classList.remove('muted');
        musicBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-volume2-icon lucide-volume-2"><path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"/><path d="M16 9a5 5 0 0 1 0 6"/><path d="M19.364 18.364a9 9 0 0 0 0-12.728"/></svg>';
    }
}

/**
 * Show music control button
 */
function showMusicControlButton() {
    const musicBtn = document.getElementById('musicBtn');
    if (musicBtn) {
        musicBtn.classList.remove('d-none');
    }
}

/**
 * Hide music control button
 * Only hides if there is no background music AND no video previews currently active.
 */
function hideMusicControlButton() {
    // Keep the button visible if any video preview is still loaded
    const hasActiveVideo = !!document.querySelector('.preview-video[data-key-uid]');
    if (hasActiveVideo) return;

    const musicBtn = document.getElementById('musicBtn');
    if (musicBtn) {
        musicBtn.classList.add('d-none');
        musicBtn.classList.remove('muted');
        musicBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-volume2-icon lucide-volume-2"><path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"/><path d="M16 9a5 5 0 0 1 0 6"/><path d="M19.364 18.364a9 9 0 0 0 0-12.728"/></svg>';
    }
}

/**
 * Get background music data for form submission
 */
window.getBackgroundMusicData = function() {
    return backgroundMusicData;
};

/**
 * Get background music file for upload
 */
window.getBackgroundMusicFile = function() {
    return backgroundMusicData.file;
};

module.exports = {
    initializeBackgroundMusicHandlers,
    getBackgroundMusicData: window.getBackgroundMusicData,
    getBackgroundMusicFile: window.getBackgroundMusicFile
};

// Expose helpers so media-upload.js can trigger music preview after selecting
// a file from the media library picker
window.displayMusicPreview           = displayMusicPreview;
window.showMusicControlButton        = showMusicControlButton;
window.saveBackgroundMusicToStorage  = saveBackgroundMusicToStorage;
window.updateIframeMusic             = updateIframeMusic;
window.toggleIframeMusic             = toggleIframeMusic;

// Stop all music when the tab is hidden; resume when the user returns
document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
        // Pause PPM preview audio
        if (musicPreviewAudio && !musicPreviewAudio.paused) {
            musicPreviewAudio.pause();
        }
        // Pause any other audio/video on the page (excluding PPM internal players)
        document.querySelectorAll('audio, video').forEach(function (el) {
            if (!el.paused && !el.closest('#ppm-popup')) el.pause();
        });
        // Pause theme iframe audio
        try {
            var iframe = document.getElementById('theme-iframe');
            if (iframe) {
                var iDoc = iframe.contentDocument ||
                           (iframe.contentWindow && iframe.contentWindow.document);
                if (iDoc) iDoc.querySelectorAll('audio, video').forEach(function (el) { el.pause(); });
                if (iframe.contentWindow) {
                    iframe.contentWindow.postMessage({ type: 'PAGE_HIDDEN' }, '*');
                }
            }
        } catch (e) {}
    } else {
        // Resume preview audio only if it was actively playing before hiding
        if (musicPreviewAudio && musicPreviewAudio.src && backgroundMusicData.fileUrl) {
            const musicBtn = document.getElementById('musicBtn');
            const wasMuted = musicBtn && musicBtn.classList.contains('muted');
            if (!wasMuted) musicPreviewAudio.play().catch(function () {});
        }
        // Resume theme iframe audio (if not muted)
        try {
            var iframe = document.getElementById('theme-iframe');
            if (iframe) {
                var muteThemeToggle = document.querySelector('[id="muteThemeMusic"]');
                var muteTheme = muteThemeToggle ? muteThemeToggle.checked : false;
                var musicBtn = document.getElementById('musicBtn');
                var isMuted = musicBtn ? musicBtn.classList.contains('muted') : false;
                if (iframe.contentWindow) {
                    iframe.contentWindow.postMessage({ type: 'PAGE_VISIBLE' }, '*');
                }
                if (!isMuted && !muteTheme) {
                    var iDoc = iframe.contentDocument ||
                               (iframe.contentWindow && iframe.contentWindow.document);
                    if (iDoc) {
                        iDoc.querySelectorAll('audio, video').forEach(function (el) {
                            if (el.paused) el.play().catch(function () {});
                        });
                    }
                }
            }
        } catch (e) {}
    }
});

